
import type { PropType } from 'vue'
import { defineComponent, computed } from 'vue'

export default defineComponent({
    props: {
        modelValue: {
            type: [Object, Array, null] as PropType<File | File[] | null>,
            default: null,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        accept: {
            type: String,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        invalid: {
            type: Boolean,
            default: false,
        }
    },
    emits: ['update:modelValue', 'change'],
    setup(props, { emit }) {
        const isSameFile = (a: File, b: File) => a.name === b.name && a.size === b.size && a.lastModified === b.lastModified && a.type === b.type

        const updateValue = (value: File | File[] | null) => {
            if (props.disabled) return

            if (props.multiple && Array.isArray(props.modelValue) && Array.isArray(value)) {
                const filtered = value.filter((newFile) => !(props.modelValue as File[]).some((file) => isSameFile(file, newFile)))
                const result = [...props.modelValue, ...filtered]

                emit('update:modelValue', result)
                emit('change', result)

                return
            }

            emit('update:modelValue', value)
            emit('change', value)
        }

        const onFileChange = (event: Event) => {
            const input = event.target as HTMLInputElement

            if (props.multiple) {
                let files = input.files ? Array.from(input.files) : []
                updateValue(files)

                input.value = ''

                return
            }

            let file = input.files ? input.files[0] : null
            updateValue(file)
            input.value = ''
        }

        const onDragOver = (event: DragEvent) => {
            event.preventDefault()
        }

        const onDrop = (event: DragEvent) => {
            event.preventDefault()

            if (props.disabled) return

            const files = event.dataTransfer?.files

            if (files) {
                if (props.multiple) {
                    updateValue(Array.from(files))
                } else {
                    updateValue(files[0])
                }
            }
        }

        const onDelete = (index?: number) => {
            if (props.disabled) return

            if (props.multiple) {
                if (typeof index === 'undefined') {
                    return
                }

                const copy = [...(props.modelValue as File[])]
                copy.splice(index, 1)

                emit('update:modelValue', copy)
                emit('change', copy)

                return
            }

            emit('update:modelValue', null)
            emit('change', null)
        }

        const formatFileSize = (sizeInBytes: number): string => {
            const kb = sizeInBytes / 1024
            const mb = kb / 1024

            if (mb >= 1) {
                return `${mb.toFixed(1)} MB`
            }

            return `${kb.toFixed(1)} KB`
        }

        const isRenderingPreview = computed(() => {
            if (props.multiple) {
                return Boolean((props.modelValue as File[]).length)
            }

            return Boolean(props.modelValue)
        })

        const acceptableFormats = computed(() => {
            if (props.accept) {
                return props.accept
            }

            return 'JPEG, PNG, GIF, MP4, PDF, PSD, AI, Word, PPT'
        })

        return {
            onFileChange,
            onDragOver,
            onDrop,
            onDelete,
            formatFileSize,
            isRenderingPreview,
            acceptableFormats
        }
    }
})
